import React, { useEffect, useRef, useState } from 'react'
import { DataContextProvider, useDataContext } from 'components/document/contexts/data'
import Editor from 'components/editor'
import { IContextMenuState, IHeadingStyle, IProcessObject, IProcessSectionObject } from 'types'
import Navigation from 'components/document/components/navigation'
import Toolbar from 'components/document/components/toolbar'
import { Triangle, Dots, Plus, Drag } from 'components/icons'
import Button from 'components/button'
import Menu from 'components/menu'
import CommentsComponent from 'components/comments'
import Image from 'components/document/components/image'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import SectionMenu from 'components/document/components/menu/views/section'
import DeleteResource from 'components/delete'
import useProject from 'hooks/useProject'
import {
  addSectionToProcess,
  findSection,
  findSectionParent,
  removeSectionFromProcess,
  getFlatData,
  doesSectionContain,
  scrollToSectionWhenReady
} from 'components/document/helpers/functions'
import { useApplicationStore } from 'hooks/application'
import api from 'helpers/api'
import { generatePath, useSearchParams, Link } from 'react-router-dom'
import { getSectionDisplayNumber } from './helpers/formatting'
import useOnScreen from 'hooks/useOnScreen'
import ArchivedWarning from 'components/archived'
import ResourceDeleted from 'views/auth/views/resourceDeleted'
import Powered from 'components/document/components/powered'
import { useAuth } from 'hooks/auth'
import { validateSourceUrl } from 'helpers/utils'
import { PROFILE } from 'routes'
import MortaAgent from 'components/agent'
import DocumentSkeleton from 'components/document/components/loadingSkeleton'
import Tooltip from 'components/tooltip'
import { SlicersProvider } from 'providers/slicers'
import { useThemedColours } from 'hooks/useTheme'

interface IDragOver {
  type: string
  source: string
  target: string
}

interface DocumentProps {
  processPublicId: string
  permissionCap?: number
  templateRender?: boolean
}

const Document: React.FC<DocumentProps> = ({ processPublicId, permissionCap, templateRender = false }) => {
  return (
    <DataContextProvider
      processPublicId={processPublicId}
      permissionCap={permissionCap}
      templateRender={templateRender}
    >
      <SlicersProvider processId={processPublicId}>
        <MortaAgent />
        <DocumentComponent />
      </SlicersProvider>
    </DataContextProvider>
  )
}

interface ProcessSectionProps {
  section: IProcessSectionObject
  sectionNumber: string
  level: number
  addSection: (parentId: string | null) => void
  addSiblingSection: (section: IProcessSectionObject) => void
  addSubsection: (section: IProcessSectionObject) => void
  dragOver: IDragOver
  setDragOver: (dragOver: IDragOver) => void
  handleDragStart: (event: React.DragEvent<HTMLDivElement>) => void
  handleDragEnter: (event: React.DragEvent<HTMLDivElement>) => void
  handleDragEnd: () => void
  formatting: IHeadingStyle
}

const ProcessSection: React.FC<ProcessSectionProps> = ({
  section,
  sectionNumber,
  level,
  addSection,
  addSiblingSection,
  addSubsection,
  dragOver,
  setDragOver,
  handleDragStart,
  handleDragEnd,
  handleDragEnter,
  formatting
}) => {
  const themed = useThemedColours()
  const {
    process,
    updateProcess,
    setSection,
    patchSection,
    sectionVisibility,
    setSectionVisibility,
    sectionExpanded,
    setSectionExpanded,
    isAdmin,
    editMode,
    focusEditorId,
    setFocusEditorId
  } = useDataContext()
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()

  const sectionRef = useRef<HTMLDivElement>(null)

  const isVisible = useOnScreen(sectionRef)
  const isSectionVisible = sectionVisibility[section.publicId] ?? false
  const isExpanded = sectionExpanded[section.publicId] ?? false

  useEffect(() => {
    if (isSectionVisible !== isVisible) {
      setSectionVisibility(section.publicId, isVisible)
    }
  }, [isVisible, section.publicId, isSectionVisible, setSectionVisibility])

  const [expandComments, setExpandComments] = useState<boolean>(false)
  const [settingsMenu, setSettingsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [deleteSectionModal, setDeleteSectionModal] = useState<boolean>(false)
  const [addMenu, setAddMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const addMenuWidth = 220
  const [archivingProcessSection, setArchivingProcessSection] = useState<{
    isArchiving: boolean
    status: string | null
  }>({
    isArchiving: false,
    status: null
  })

  const settingsWidth = 300

  const deleteSection = async (): Promise<void> => {
    if (!process || !section) return

    if (archivingProcessSection.isArchiving) {
      // Already running – prevent duplicate submits
      return Promise.resolve()
    }

    setArchivingProcessSection({ isArchiving: true, status: null })

    try {
      await api.deleteProcessSection(project.publicId, process.publicId, section.publicId)
      let children = [...process.children]
      children = removeSectionFromProcess(section.publicId, children)
      updateProcess({ ...process, children })
      setArchivingProcessSection({ isArchiving: false, status: 'success' })
    } catch (error) {
      displayErrorMessage(error)
      setArchivingProcessSection({ isArchiving: false, status: 'error' })
      return Promise.reject(error)
    }
  }

  const dragId = `${dragOver.type}/${dragOver.target}`
  const aboveId = `above/${section.publicId}`
  const withinId = `within/${section.publicId}`
  const belowId = `below/${section.publicId}`
  const dragHeights = 4
  const draggingSelf = dragOver.source === section.publicId
  const sectionLabel = section.name ? `"${section.name}"` : 'this section'
  const aboveTooltip = `Place above ${sectionLabel}`
  const belowTooltip = `Place below ${sectionLabel}`
  const withinTooltip = `Nest as a subsection of ${sectionLabel}`
  return (
    <div>
      <div className="relative select-none my-[12.5px] mx-0">
        <div ref={sectionRef} id={`section/${section.publicId}`} className="flex items-center py-[5px] px-0.5">
          {/*

        ----------------
        Dragging Areas
        ----------------

        */}

          <Tooltip content={aboveTooltip} visible={dragId === aboveId && !draggingSelf}>
            <div
              id={aboveId}
              className="absolute w-full top-0"
              style={{
                height: `${dragHeights}px`,
                backgroundColor: dragId === aboveId && !draggingSelf ? project.primaryColour : 'var(--color-surface)'
              }}
              onDragEnter={handleDragEnter}
            />
          </Tooltip>

          <Tooltip content={belowTooltip} visible={dragId === belowId && !draggingSelf}>
            <div
              id={belowId}
              className="absolute w-full"
              style={{
                top: `calc(62px - ${dragHeights}px)`,
                height: `${dragHeights}px`,
                backgroundColor: dragId === belowId && !draggingSelf ? project.primaryColour : 'var(--color-surface)'
              }}
              onDragEnter={handleDragEnter}
            />
          </Tooltip>

          {/*

        ----------------
        Section Content
        ----------------

        */}

          <Tooltip content={withinTooltip} visible={dragId === withinId && !draggingSelf}>
            <div
              id={withinId}
              className="group relative flex items-center w-full"
              style={{ border: dragId === withinId ? `2px solid ${project.primaryColour}` : '2px solid transparent' }}
              draggable={true}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragEnter={handleDragEnter}
            >
              {isAdmin && editMode && (
                // Anchored into the empty left margin (right-full = just left of the row) so the section
                // text keeps its original indent; revealed on hover of the row, and kept visible while
                // the add menu is open (the pointer is over the menu, off the row, so group-hover drops).
                <div
                  className={`absolute right-full top-1/2 flex -translate-y-[calc(50%+2px)] items-center pr-1 transition-opacity ${
                    addMenu.open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <div
                    draggable={true}
                    onDragStart={(event) => event.preventDefault()}
                    className="flex items-center justify-center rounded select-none cursor-pointer text-muted hover:bg-surface-sunken p-1"
                    title={'Add section or subsection'}
                    data-testid={`add-control-${section.publicId}`}
                    onClick={(event: React.MouseEvent) => {
                      const rect = event.currentTarget.getBoundingClientRect()
                      setAddMenu({
                        open: true,
                        top: `${rect.bottom + 6}px`,
                        left: `${rect.left}px`,
                        right: 'auto',
                        bottom: 'auto'
                      })
                    }}
                  >
                    <Plus />
                  </div>
                  <div
                    className="flex items-center justify-center rounded select-none cursor-grab active:cursor-grabbing text-muted hover:bg-surface-sunken p-1"
                    title="Drag to reorder section"
                    data-testid={`reorder-handle-${section.publicId}`}
                  >
                    <Drag />
                  </div>
                </div>
              )}
              <div
                className="flex items-center justify-center rounded p-2.5 select-none transition-all cursor-pointer hover:bg-surface-sunken py-2.5 pr-2.5 pl-0"
                title={isExpanded ? 'Collapse Section' : 'Expand Section'}
                onClick={() => setSectionExpanded(section.publicId, !isExpanded)}
              >
                <div className="text-muted">
                  <Triangle type={isExpanded ? 'down' : 'right'} />
                </div>
              </div>
              <div className="flex w-full" draggable={true} onDragStart={(event) => event.preventDefault()}>
                <div
                  className={`flex flex-col justify-center ${formatting.bold ? 'font-bold' : ''} ${
                    formatting.italic ? 'italic' : ''
                  } ${formatting.underline ? 'underline' : ''}`}
                  style={{ color: themed.onSurface(formatting.colour), fontSize: `${formatting.fontSize * 100}%` }}
                >
                  {sectionNumber}&nbsp;
                </div>
                <input
                  className={`${formatting.bold ? 'font-bold' : ''} ${formatting.italic ? 'italic' : ''} ${
                    formatting.underline ? 'underline' : ''
                  } rounded transition-all w-full box-border ${editMode ? 'border' : 'border-0'} bg-surface p-0`}
                  id={`process-section-${section.publicId}-input`}
                  style={{
                    color: themed.onSurface(formatting.colour),
                    fontSize: `${formatting.fontSize * 100}%`,
                    transition: 'all 0s'
                  }}
                  readOnly={!isAdmin || !editMode}
                  value={section.name}
                  onChange={(event) => setSection({ ...section, name: event.target.value })}
                />
              </div>

              {!process?.templateRender && process?.allowComments && !editMode && (
                <div
                  draggable={true}
                  onDragStart={(event) => event.preventDefault()}
                  className="flex items-center justify-center rounded select-none transition-all cursor-pointer hover:bg-surface-sunken ml-auto py-2.5 px-1"
                  title="Section Comments"
                >
                  <CommentsComponent
                    selected={expandComments}
                    referenceId={section.publicId}
                    referenceType="process_section"
                    openCommentThreads={section.openCommentThreads}
                    permissionLevel={process ? process.permissionLevel : 0}
                    onSelectedComment={() => {
                      setExpandComments(true)
                    }}
                    onCommentThreadsChange={() => {
                      void 0
                    }}
                  />
                </div>
              )}

              {!process?.templateRender && (
                <div
                  draggable={true}
                  onDragStart={(event) => event.preventDefault()}
                  className="flex items-center justify-center rounded select-none transition-all cursor-pointer hover:bg-surface-sunken py-2.5 px-1"
                  title={'Section Settings'}
                  onClick={(event: React.MouseEvent) =>
                    setSettingsMenu({
                      open: true,
                      top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
                      left: `${event.currentTarget.getBoundingClientRect().left - settingsWidth + 20}px`,
                      right: 'auto',
                      bottom: 'auto'
                    })
                  }
                >
                  <Dots />
                </div>
              )}
            </div>
          </Tooltip>
        </div>

        {isExpanded && (
          <div>
            {((isAdmin && editMode) ||
              (isAdmin &&
                section.description &&
                !(
                  section.description.content.blocks.length === 1 && section.description.content.blocks[0].text === ''
                )) ||
              (!isAdmin && section.description)) &&
              process && (
                <Editor
                  databaseDoc={section.description}
                  readOnly={!isAdmin || !editMode}
                  editorId={section.publicId}
                  focusWhenEditable={focusEditorId === section.publicId}
                  onFocusedWhenEditable={() => setFocusEditorId(null)}
                  onChange={(content) => {
                    // Only the text: the section here can be a render behind (e.g. an embed just saved
                    // a new submission in the same tick).
                    patchSection(section.publicId, { description: content })
                  }}
                  border={editMode}
                  process={process}
                  processSectionId={section.publicId}
                  resources={[{ resource: 'process', publicId: process.publicId }]}
                />
              )}
          </div>
        )}
      </div>
      {isExpanded &&
        section.children.map((subsection: IProcessSectionObject, subsectionNumber: number) => {
          const subsectionFormatting = project.headingStyles[level] ? project.headingStyles[level] : formatting
          const subsectionDisplayNumber = getSectionDisplayNumber(sectionNumber, subsectionNumber, subsectionFormatting)
          return (
            <ProcessSection
              key={subsection.publicId}
              section={subsection}
              sectionNumber={subsectionDisplayNumber}
              level={level + 1}
              addSection={addSection}
              addSiblingSection={addSiblingSection}
              addSubsection={addSubsection}
              dragOver={dragOver}
              setDragOver={setDragOver}
              handleDragEnd={handleDragEnd}
              handleDragEnter={handleDragEnter}
              handleDragStart={handleDragStart}
              formatting={subsectionFormatting}
            />
          )
        })}

      <SectionMenu
        id={`section-settings-context-menu`}
        menuState={settingsMenu}
        setMenuState={setSettingsMenu}
        width={settingsWidth}
        section={section}
        setDeleteSectionModal={setDeleteSectionModal}
        addSection={addSection}
        sectionNumber={sectionNumber}
      />

      {addMenu.open && (
        <Menu
          id={`add-section-menu-${section.publicId}`}
          menuState={addMenu}
          setMenuState={setAddMenu}
          width={addMenuWidth}
        >
          <div className="p-0 m-0 list-none">
            {[
              { key: 'section', label: 'Add section below', onSelect: () => addSiblingSection(section) },
              { key: 'subsection', label: 'Add subsection below', onSelect: () => addSubsection(section) }
            ].map((option) => (
              <li key={option.key}>
                <div
                  className="relative flex items-center rounded text-body select-none transition-all truncate cursor-pointer hover:bg-surface-sunken h-8 leading-[32px] py-0 px-2.5"
                  data-testid={`add-${option.key}-below-${section.publicId}`}
                  onClick={() => {
                    option.onSelect()
                    setAddMenu(INITIAL_CONTEXT_MENU_STATE)
                  }}
                >
                  <Plus />
                  <span className="ml-2.5">{option.label}</span>
                </div>
              </li>
            ))}
          </div>
        </Menu>
      )}

      {deleteSectionModal && (
        <DeleteResource
          id="delete-process-section-modal"
          open={deleteSectionModal}
          setOpen={setDeleteSectionModal}
          resourceName={section.name}
          deleteResource={deleteSection}
        />
      )}
    </div>
  )
}

const DocumentComponent: React.FC = () => {
  const themed = useThemedColours()
  const {
    process,
    updateProcess,
    isAdmin,
    editMode,
    setEditMode,
    focusEditorId,
    setFocusEditorId,
    getLoadId,
    navigationMenu,
    expandAllSections,
    setSectionExpanded,
    errorStatusCode
  } = useDataContext()
  const { user } = useAuth()
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [searchParams] = useSearchParams()
  const [dragOver, setDragOver] = useState<IDragOver>({ type: '', source: '', target: '' })
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (editMode) {
      const id = event.currentTarget.id
      setDragOver({ ...dragOver, source: id.split('/')[1] })
    }
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (editMode && dragOver.source !== '') {
      const targetSectionId = event.currentTarget.id
      setDragOver({ ...dragOver, type: targetSectionId.split('/')[0], target: targetSectionId.split('/')[1] })
    }
  }

  const handleDragEnd = () => {
    if (editMode && dragOver.target !== '' && dragOver.source !== '' && dragOver.source !== dragOver.target) {
      const type = dragOver.type
      const source = dragOver.source
      const target = dragOver.target

      setDragOver({ type: '', target: '', source: '' })

      if (process) {
        const sectionParent = findSectionParent(source, process)
        const sourceSection = findSection(source, process)
        const targetSection = findSection(target, process)
        const targetParentSection = findSectionParent(target, process)

        // If same parent then cancel
        if (type === 'within' && sectionParent === target) return

        let children = [...process.children]
        children = removeSectionFromProcess(source, children)

        if (type === 'within') {
          const parentId = targetSection.section ? targetSection.section.publicId : null
          children = addSectionToProcess(parentId, children, { ...sourceSection.section, parentId })
        } else if (type === 'above') {
          if (targetSection.index - 1 === sourceSection.index) return
          const parentId = targetParentSection && targetParentSection !== process.publicId ? targetParentSection : null
          children = addSectionToProcess(parentId, children, sourceSection.section, targetSection.index)
        } else if (type === 'below') {
          if (targetSection.index + 1 === sourceSection.index) return
          const parentId = targetParentSection && targetParentSection !== process.publicId ? targetParentSection : null
          children = addSectionToProcess(parentId, children, sourceSection.section, targetSection.index)
        }

        const newProcess = { ...process, children: children }
        updateProcess(newProcess, true)

        const processSections = getFlatData(newProcess)
        api
          .updateProcessSectionOrder({
            processSections,
            processId: process.publicId,
            context: { projectId: project.publicId }
          })

          .catch((error) => {
            displayErrorMessage(error)
          })
      }
    } else {
      setDragOver({ type: '', target: '', source: '' })
    }
  }

  const urlSectionId = searchParams.get('section')
  const autoExpandSections = searchParams.get('expandSections')

  const expandSectionsToTarget = (parent: IProcessObject | IProcessSectionObject, sectionId: string) => {
    for (let i = 0; i < parent.children.length; i++) {
      const section = parent.children[i]

      if (section.publicId === sectionId || doesSectionContain(section, sectionId)) {
        setSectionExpanded(section.publicId, true)
      }
      expandSectionsToTarget(section, sectionId)
    }
  }

  useEffect(() => {
    if (!process || !urlSectionId) return

    let cancelScroll: (() => void) | undefined
    const rafId = requestAnimationFrame(() => {
      expandSectionsToTarget(process, urlSectionId)
      cancelScroll = scrollToSectionWhenReady(urlSectionId)
    })

    return () => {
      cancelAnimationFrame(rafId)
      cancelScroll?.()
    }
  }, [process?.publicId, urlSectionId])

  const expandAllSectionsRef = useRef(expandAllSections)
  useEffect(() => {
    expandAllSectionsRef.current = expandAllSections
  }, [expandAllSections])

  useEffect(() => {
    if (process && process.children.length > 0 && (process.expandByDefault || autoExpandSections)) {
      expandAllSectionsRef.current()
    }
  }, [process?.publicId, autoExpandSections])

  // Every new section opens expanded, in edit mode, with the cursor in its (empty) description editor.
  const createSection = (parentId: null | string, position?: number): void => {
    if (process) {
      // Dropped if the user has since opened another document, or left and come back to this one
      const load = getLoadId()
      api
        .createNewProcessSection({
          name: 'New Document Section',
          processId: process.publicId,
          parentId: parentId,
          context: { projectId: project.publicId }
        })
        .then((response) => {
          if (getLoadId() !== load) return
          setSnackbarMessage({ status: 'success' })
          const children = addSectionToProcess(parentId, [...process.children], response.data, position)
          const newProcess = { ...process, children: children }
          updateProcess(newProcess)

          // The backend always appends the new section to the end of its parent. When we asked for a
          // specific position (the "Add section below" flow), persist the reordered tree so the new
          // section stays directly below the chosen heading on reload — no manual reorder needed.
          if (position !== undefined) {
            api
              .updateProcessSectionOrder({
                processSections: getFlatData(newProcess),
                processId: process.publicId,
                context: { projectId: project.publicId }
              })
              .catch((error) => {
                displayErrorMessage(error)
              })
          }

          setEditMode(true)
          setSectionExpanded(response.data.publicId, true)
          setFocusEditorId(response.data.publicId)
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  // The top-right cog ("Add Section", parentId null) and a heading's three-dots menu ("Add Subsection",
  // parentId set) append to the end of the parent.
  const addSection = (parentId: null | string): void => createSection(parentId)

  // Heading "+" menu → "Add section below": insert a sibling at the same level, directly below the
  // chosen heading (position persisted), so users can add sections mid-document without reordering.
  const addSiblingSection = (section: IProcessSectionObject): void => {
    if (!process) return
    const parent = findSectionParent(section.publicId, process)
    const parentId = parent && parent !== process.publicId ? parent : null
    const { index } = findSection(section.publicId, process)
    createSection(parentId, index + 1)
  }

  // Heading "+" menu → "Add subsection below": nest a new subsection under the chosen heading,
  // expanding it so the new subsection is visible.
  const addSubsection = (section: IProcessSectionObject): void => {
    setSectionExpanded(section.publicId, true)
    createSection(section.publicId)
  }

  const dragId = `${dragOver.type}/${dragOver.target}`
  const withinProcessId = `within/${process ? process.publicId : ''}`
  const formatting = project.headingStyles[0]
    ? project.headingStyles[0]
    : {
        fontSize: 1.4,
        colour: project.processTitleColour,
        bold: project.processTitleBold,
        italic: project.processTitleItalic,
        underline: project.processTitleUnderline
      }

  if (
    process &&
    !process.templateRender &&
    process.projectPublicId !== '' &&
    project.publicId !== '' &&
    process.projectPublicId !== project.publicId
  ) {
    return <ResourceDeleted />
  } else if (process && errorStatusCode === null)
    return (
      <div className="flex flex-row flex-wrap w-full h-full bg-surface overflow-auto">
        {process.isDeleted && <ArchivedWarning message={'This resource has been archived'} />}
        {navigationMenu && <Navigation />}
        <div className="flex flex-col w-full h-full overflow-hidden text-body">
          {/* The header is the column's first row, outside the scroller, so it
              stays put while the document scrolls beneath it. It's a container, so it can
              close up on a narrow page (beside the navigation panel the window's width isn't
              the header's). Only the header: a container also holds anything fixed inside it,
              such as a table opened full screen. */}
          {!process?.templateRender && (
            <div className="@container/document shrink-0">
              <Toolbar addSection={addSection} />
            </div>
          )}
          <div className="overflow-auto select-none bg-surface h-full">
            <Image />

            <div
              className="relative mt-[30px] mb-[75px] mx-[150px] max-lg:mt-10 max-lg:mb-[75px] max-lg:mx-[30px]"
              data-testid="document"
            >
              {isAdmin && editMode ? (
                <input
                  id={withinProcessId}
                  className={`${project.processTitleBold ? 'font-bold' : ''} ${
                    project.processTitleItalic ? 'italic' : ''
                  } ${
                    project.processTitleUnderline ? 'underline' : ''
                  } rounded transition-all w-full box-border border-0 bg-surface p-0`}
                  style={{
                    color: themed.onSurface(project.processTitleColour),
                    fontSize: `${project.processTitleFontSize * 100}%`,
                    border: dragId === withinProcessId ? `2px solid ${project.primaryColour}` : '1px solid black'
                  }}
                  value={process.name}
                  onChange={(event) => updateProcess({ ...process, name: event.target.value })}
                  onDragEnter={handleDragEnter}
                />
              ) : (
                <div
                  className={`${project.processTitleBold ? 'font-bold' : ''} ${
                    project.processTitleItalic ? 'italic' : ''
                  } ${
                    project.processTitleUnderline ? 'underline' : ''
                  } rounded transition-all w-full box-border border-0 bg-surface p-0 select-text h-fit`}
                  style={{
                    color: themed.onSurface(project.processTitleColour),
                    fontSize: `${project.processTitleFontSize * 100}%`,
                    lineHeight: '1em',
                    border: dragId === withinProcessId ? `2px solid ${project.primaryColour}` : '2px solid transparent',
                    textAlign: project.processTitleAlignment
                  }}
                >
                  {process.name}
                </div>
              )}

              {process.createdBy && (
                <div className="flex items-center mt-[30px] mb-[30px]">
                  <img
                    className="rounded-full h-[25px] w-[25px]"
                    src={validateSourceUrl(process.createdBy?.profilePicture)}
                    alt={process.createdBy?.profilePicture || 'Anonymous user'}
                  />
                  <div className="text-muted ml-[5px]">
                    Created by{' '}
                    {process.createdBy.firebaseUserId ? (
                      <Link
                        to={generatePath(PROFILE, {
                          userId: process.createdBy.firebaseUserId,
                          tab: 'information'
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {process.createdBy.name}
                      </Link>
                    ) : (
                      // Some creators have no firebaseUserId (e.g. deleted
                      // accounts) — generatePath would throw and crash the
                      // page (Sentry WEBAPP-2G)
                      <span>{process.createdBy.name}</span>
                    )}
                  </div>
                </div>
              )}

              {((isAdmin && editMode) ||
                (isAdmin &&
                  process.description &&
                  !(
                    process.description.content.blocks.length === 1 && process.description.content.blocks[0].text === ''
                  )) ||
                (!isAdmin && process.description)) && (
                <Editor
                  databaseDoc={process.description ? process.description : null}
                  readOnly={!isAdmin || !editMode}
                  editorId={process.publicId}
                  focusWhenEditable={focusEditorId === process.publicId}
                  onFocusedWhenEditable={() => setFocusEditorId(null)}
                  onChange={(content) => {
                    updateProcess({ ...process, description: content })
                  }}
                  border={editMode}
                  process={process}
                  resources={[{ resource: 'process', publicId: process.publicId }]}
                />
              )}

              {process.children.map((section: IProcessSectionObject, sectionNumber: number) => {
                const sectionDisplayNumber = getSectionDisplayNumber('', sectionNumber, formatting)
                return (
                  <ProcessSection
                    key={section.publicId}
                    section={section}
                    sectionNumber={sectionDisplayNumber}
                    level={1}
                    addSection={addSection}
                    addSiblingSection={addSiblingSection}
                    addSubsection={addSubsection}
                    dragOver={dragOver}
                    setDragOver={setDragOver}
                    handleDragStart={handleDragStart}
                    handleDragEnd={handleDragEnd}
                    handleDragEnter={handleDragEnter}
                    formatting={formatting}
                  />
                )
              })}

              {isAdmin && editMode && (
                <Button
                  internalType="outline"
                  data-testid="add-section-button"
                  onClick={() => addSection(null)}
                  className="h-auto w-full mt-2.5 gap-2 border-dashed py-2.5 text-muted hover:bg-surface-sunken hover:text-body"
                >
                  <Plus />
                  <span>Add section</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {!user && <Powered />}
      </div>
    )
  else if (errorStatusCode === 404) {
    return <ResourceDeleted />
  } else {
    return <DocumentSkeleton />
  }
}

export default Document
